import { Component, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MdbDropdownModule } from 'mdb-angular-ui-kit/dropdown';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';  // Import FormsModule here
import { GridApi } from 'ag-grid-enterprise';
import { MatSidenav } from '@angular/material/sidenav';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { trigger, transition, style, animate } from '@angular/animations';
import { Bookmark, BookmarkVisibility } from '../../models/bookmark';
import { AppraisalDataService } from '../../shared/services/appraisal-data.service';
import { GraphUser, UserService } from '../../shared/services/user.service';
import { BookmarksService } from '../../shared/services/bookmarks.service';
import { finalize, Observable } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
@Component({
  selector: 'app-bookmarks',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MdbDropdownModule, MatCardModule, MatExpansionModule,
    MatInputModule, MatAutocompleteModule, MatProgressSpinnerModule],
  templateUrl: './bookmarks.component.html',
  styleUrl: './bookmarks.component.scss',
  animations: [
    trigger('formState', [
      transition('closed => open', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('500ms ease-in-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition('open => closed', [
        animate('300ms ease-in-out', style({ opacity: 0, transform: 'translateY(-20px)' }))
      ])
    ])
  ]
})
export class BookmarksComponent {
  @Output() loadBookmarkInGrid = new EventEmitter<void>();
  @Input() bookmarkGrid: string = '';
  bookmarks: Bookmark[] = [];
  filteredBookmarks: Bookmark[] = [];
  selectedBookmark: Bookmark | null = null;

  bookmarkName = '';
  isPublic = false;
  isDefault = false;

  searchQuery = '';
  isFormOpen = false;
  newMode = false;

  successMessage: string = '';  // Flag for the success message
  successMessageVisible: boolean = false; // Flag to control visibility

  currentPage: number = 1;
  itemsPerPage: number = 5;

  errorMessage: string = '';
  errorMessageVisible: boolean = false;

  isGlobalAdmin: boolean = false;
  isPaginationEnabled: boolean = false;

  isLoading = false;
  isSaving = false;

  currentUserId!: string;
  constructor(private appraisalDataService: AppraisalDataService, private userService: UserService,
    private bookmarksService: BookmarksService
  ) {
  }
  ngOnInit() {
    this.userService.userData$.subscribe((data) => {
      this.currentUserId = this.userService.getUserData().userId;
      this.isGlobalAdmin = this.userService.getUserData().roles.includes('GlobalAdmin');
      this.fetchBookmarks();
    });
  }

  fetchBookmarks() {
    this.isLoading = true;
    let userId = this.userService.getUserData().userId;
    this.bookmarksService.fetchBookmarks(userId).pipe(finalize(() => this.isLoading = false)).subscribe((data: any) => {
      this.bookmarks = data.filter((x:any) => x.gridId == this.bookmarkGrid).map((x: any) => {
        return this.toBookmark(x);
      });
      // this.loadCreatorEmails();
      this.sortBookmarks();
      this.filteredBookmarks = [...this.bookmarks];

       // find the default (user’s) bookmark
      const defaultBm = this.bookmarks.find(b => b.isDefault && b.createdById === userId);
      if (defaultBm) {
        // 1) restore its grid state
        const state = JSON.parse(defaultBm.gridState);
        this.appraisalDataService.setGridStateData(state);
        this.loadBookmarkInGrid.emit();

        // 2) mark it as “selected” in the list
        this.selectedBookmark = defaultBm;
      }
    });
  }

  toBookmark(e: any): Bookmark {
    return {
      bookmarkName: e.bookmarkName,
      createdById: e.createdById,
      isDefault: e.isDefault,
      visibility: e.visibility,
      isAdminCreated: e.isAdminCreated,
      gridState: e.gridState,
      createdAt: new Date(e.createdDate),
      modifiedAt: new Date(e.lastUpdated),
      rowKey: e.rowKey
    };
  }

  sortBookmarks() {
    this.bookmarks.sort((a, b) => {
      const aIsMyDefault = a.isDefault && a.createdById === this.currentUserId;
      const bIsMyDefault = b.isDefault && b.createdById === this.currentUserId;
      // 1. My default bookmark always wins
      if (aIsMyDefault !== bIsMyDefault) {
        return aIsMyDefault ? -1 : 1;
      }
      // 2. Then any other default bookmarks (public or private)
      if (a.isDefault !== b.isDefault) {
        return a.isDefault ? -1 : 1;
      }
      // 3. Finally by creation date (newest first)
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }


  // Opens the form to add a new bookmark
  openBookmarkForm() {
    this.isFormOpen = true;
    this.newMode = true; // Set mode to "Add New"
    this.selectedBookmark = null;

    const idx = this.bookmarks.length + 1;
    this.bookmarkName = `Bookmark ${idx}`;

    this.isPublic = false;
    this.isDefault = false;
  }

  // Loads the selected bookmark for editing
  loadBookmark(bm: Bookmark) {

    // if it’s public and not yours, bail out
    if (bm.visibility === 'Public' && bm.createdById !== this.currentUserId) {
      this.isFormOpen = false;
      this.selectedBookmark = bm;
      // apply saved grid state
      const state = JSON.parse(bm.gridState);
      this.appraisalDataService.setGridStateData(state);
      this.loadBookmarkInGrid.emit();
      return;
    }

    this.newMode = false;
    this.selectedBookmark = bm;
    this.bookmarkName = bm.bookmarkName;
    this.isDefault = bm.isDefault;
    this.isPublic = bm.visibility === 'Public';

    // apply saved grid state
    const state = JSON.parse(bm.gridState);
    this.appraisalDataService.setGridStateData(state);
    this.loadBookmarkInGrid.emit();

    this.isFormOpen = true;
  }

  // Save the new or updated bookmark
  saveBookmark() {

    // if editing an existing PUBLIC bookmark that isn’t yours, stop
    if (!this.newMode &&
      this.selectedBookmark!.visibility === 'Public' &&
      this.selectedBookmark!.createdById !== this.currentUserId) {
      this.showError('You cannot edit a public bookmark you did not create.');
      return;
    }

    this.isLoading = true;
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    const trimmedName = this.bookmarkName.trim();
    if (!trimmedName) {
      this.showError('Name cannot be empty');
      return;
    }

    // duplicate check...
    const duplicate = this.bookmarks.some(b => {
      const sameName = b.bookmarkName.trim().toLowerCase() === trimmedName.toLowerCase();
      return sameName && (this.newMode || b.rowKey !== this.selectedBookmark?.rowKey);
    });
    if (duplicate) {
      this.showError('A bookmark with that name already exists.');
      this.isLoading = false;
      this.isSaving = false;
      return;
    }
    this.clearError();

    if (this.isDefault) {
      this.bookmarks
        .filter(b => b.createdById === this.currentUserId)  // only your bookmarks
        .forEach(b => b.isDefault = false);
    }

    let bmGridState = this.appraisalDataService.getGridStateData();
    const { columnSizing, ...omittedGridState } = bmGridState as any;
    
    const bm: Bookmark = {
      bookmarkName: this.bookmarkName,
      bookmarkGrid: this.bookmarkGrid,
      createdById: this.userService.getUserData().userId,
      isDefault: this.isDefault,
      visibility: this.isPublic ? "Public" : "Private",
      isAdminCreated: false,
      gridState: JSON.stringify(omittedGridState),
      createdAt: this.newMode ? new Date() : this.selectedBookmark!.createdAt,
      modifiedAt: new Date(),
      rowKey: this.selectedBookmark?.rowKey,
    };

    if (this.newMode) {
      this.bookmarks.unshift(bm);
    } else if (this.selectedBookmark) {
      const idx = this.bookmarks.findIndex(x => x.bookmarkName === this.selectedBookmark!.bookmarkName);
      if (idx > -1) this.bookmarks[idx] = bm;
    }

    const call$: Observable<{ rowKey: string }> = this.newMode
      ? this.bookmarksService.saveBookmark(bm)
      : this.bookmarksService.updateBookmark(bm);

    call$.pipe(finalize(() => {
      this.isLoading = false; this.isSaving = false
    })).subscribe({
      next: (data: any) => {
        if (this.newMode) {
          this.bookmarks[0].rowKey = data.rowKey;
        }
        this.filteredBookmarks = [...this.bookmarks];
        this.sortBookmarks();
        this.showSuccessMessage();
        this.isFormOpen = false;
      },
      error: err => {
        console.error('Bookmark save failed', err);
        this.showError('Could not save bookmark on server.');
      }
    });
  }

  private showError(msg: string) {
    this.errorMessage = msg;
    this.errorMessageVisible = true;
    setTimeout(() => (this.errorMessageVisible = false), 3000);
  }

  private clearError() {
    this.errorMessageVisible = false;
    this.errorMessage = '';
  }

  showSuccessMessage() {
    this.successMessage = this.newMode ? 'Bookmark saved successfully!' : 'Bookmark updated successfully!';
    this.successMessageVisible = true;

    // Hide success message after 3 seconds
    setTimeout(() => {
      this.successMessageVisible = false;
    }, 3000);
  }

  // Cancel form
  cancel() {
    this.isFormOpen = false;
  }

  // Filter bookmarks based on search query
  filterBookmarks() {
    const q = this.searchQuery.toLowerCase().trim();

    // Filter bookmarks based on search query (fuzzy search)
    this.filteredBookmarks = this.bookmarks.filter(b => {
      return b.bookmarkName.toLowerCase().includes(q); // Case-insensitive fuzzy search
    });

    this.filteredBookmarks.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(); // Sorting by 'createdAt'
    });
  }

  // Function to display bookmarks in the autocomplete dropdown
  displayFn(bookmark: string): string {
    return bookmark ? bookmark : '';
  }

  private async loadCreatorEmails() {
    // gather all unique creator IDs for PUBLIC bookmarks
    const publicIds = Array.from(new Set(
      this.bookmarks
        .filter(b => b.visibility === 'Public')
        .map(b => b.createdById)
    ));
    if (!publicIds.length) return;

    // you can batch-fetch
    try {
      // await the batch-Promise
      const batch = await this.userService.getUsersBatch(publicIds);
      batch.responses.forEach(res => {
        if (res.status === 200) {
          const u: GraphUser = res.body;
          this.bookmarks
            .filter(b => b.createdById === u.id)
            .forEach(b => b.createdByEmail = u.mail || u.userPrincipalName);
        }
      });
      this.filteredBookmarks = [...this.bookmarks];
    }
    catch (err) {
      console.error('Failed to load creator emails', err);
    }
  }
}
